import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from '../api/axios';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);
  const navigate = useNavigate();

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      setError('קישור לא תקין');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('הסיסמאות אינן תואמות');
      return;
    }

    if (password.length < 6) {
      setError('הסיסמה חייבת להכיל לפחות 6 תווים');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post('/auth/reset-password', {
        token,
        newPassword: password
      });
      
      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(response.data.error || 'אירעה שגיאה');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'אירעה שגיאה באיפוס הסיסמה');
    } finally {
      setLoading(false);
    }
  };

  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-nexolog-navy to-nexolog-teal flex items-center justify-center px-4" dir="rtl">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-white mb-2">Nexolog</h1>
            <p className="text-white/80 text-lg">מערכת ניהול עמלות נהגים</p>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="text-center">
              <div className="mb-4">
                <svg className="w-16 h-16 text-red-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-nexolog-navy mb-4">
                קישור לא תקין
              </h2>
              <p className="text-gray-600 mb-6">
                הקישור לאיפוס סיסמה אינו תקין או פג תוקפו.
              </p>
              <button
                onClick={() => navigate('/forgot-password')}
                className="w-full bg-nexolog-teal hover:bg-nexolog-teal/90 text-white font-medium py-3 px-4 rounded-lg transition-colors"
              >
                בקש קישור חדש
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-nexolog-navy to-nexolog-teal flex items-center justify-center px-4" dir="rtl">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-white mb-2">Nexolog</h1>
            <p className="text-white/80 text-lg">מערכת ניהול עמלות נהגים</p>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="text-center">
              <div className="mb-4">
                <svg className="w-16 h-16 text-green-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-nexolog-navy mb-4">
                הסיסמה שונתה בהצלחה!
              </h2>
              <p className="text-gray-600 mb-6">
                עכשיו אתה יכול להתחבר עם הסיסמה החדשה.
                <br />
                מעביר אותך לדף ההתחברות...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-nexolog-navy to-nexolog-teal flex items-center justify-center px-4" dir="rtl">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Nexolog</h1>
          <p className="text-white/80 text-lg">מערכת ניהול משכורות נהגים</p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <h2 className="text-2xl font-bold text-center text-nexolog-navy mb-2">
            איפוס סיסמה
          </h2>
          <p className="text-center text-gray-600 mb-6 text-sm">
            הזן סיסמה חדשה לחשבון שלך
          </p>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm text-center">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                סיסמה חדשה
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-nexolog-teal focus:border-transparent transition-all"
                required
                autoFocus
                minLength="6"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                אימות סיסמה
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-nexolog-teal focus:border-transparent transition-all"
                required
                minLength="6"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-nexolog-teal hover:bg-nexolog-teal/90 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'מאפס סיסמה...' : 'אפס סיסמה'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/login')}
              className="text-sm text-nexolog-teal hover:underline"
            >
              חזור להתחברות
            </button>
          </div>
        </div>

        <div className="text-center mt-6 text-white/60 text-sm">
          © 2025 Nexolog. כל הזכויות שמורות.
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;

